import { useState } from 'react';
import type { IconSlug } from '../lib/icons';
import { useStore } from '../lib/store';
import {
  TINT_KEYS,
  type GoalType,
  type Tint,
} from '../lib/types';
import { CategoryIcon } from './CategoryIcon';
import { Choice } from './Choice';
import { DatePicker } from './DatePicker';
import { Field } from './Field';
import { IconPicker } from './IconPicker';
import { PixelButton } from './PixelButton';
import { Triangle } from './Triangle';

interface FormState {
  title: string;
  type: GoalType;
  cat: string;
  note: string;
  dueDate: string | null;
}

export function AddQuestForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const { createGoal, categories, categoryMap } = useStore();
  const [form, setForm] = useState<FormState>(() => ({
    title: '',
    type: 'todo',
    cat: categories[0]?.key ?? '',
    note: '',
    dueDate: null,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [makingNew, setMakingNew] = useState(false);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm({ ...form, [k]: v });

  const submit = async () => {
    if (saving) return;
    const title = form.title.trim();
    if (!title) {
      setErr('Give your chore a name.');
      return;
    }
    if (!form.cat) {
      setErr('Pick a category first.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await createGoal({
        title,
        type: form.type,
        cat: form.cat,
        note: form.note,
        due_date: form.type === 'todo' ? form.dueDate : null,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Field label="CHORE NAME">
        <input
          className="pixel-input"
          value={form.title}
          onChange={e => update('title', e.target.value)}
          placeholder="Morning walk around the block"
          autoFocus
        />
      </Field>
      <Field label="CHORE TYPE">
        <div style={{ display: 'flex', gap: 10 }}>
          <Choice
            selected={form.type === 'habit'}
            tint="gold"
            onClick={() => update('type', 'habit')}
          >
            DAILY HABIT
          </Choice>
          <Choice
            selected={form.type === 'todo'}
            tint="gold"
            onClick={() => update('type', 'todo')}
          >
            ONE-OFF ERRAND
          </Choice>
        </div>
      </Field>
      <Field label="CATEGORY">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {categories.map(c => (
            <Choice
              key={c.id}
              selected={form.cat === c.key}
              tint={c.tint}
              onClick={() => update('cat', c.key)}
            >
              <CategoryIcon slug={c.icon} size={16} alt="" />
              {c.label.toUpperCase()}
            </Choice>
          ))}
          <Choice
            selected={makingNew}
            onClick={() => setMakingNew(v => !v)}
          >
            + NEW
          </Choice>
        </div>
        {makingNew && (
          <NewCategoryForm
            onCancel={() => setMakingNew(false)}
            onCreated={key => {
              update('cat', key);
              setMakingNew(false);
            }}
          />
        )}
        {!makingNew && form.cat && !categoryMap.has(form.cat) && (
          <div style={{ fontFamily: 'VT323', fontSize: 16, color: '#b83a26', marginTop: 6 }}>
            Hmm, that category's gone. Pick another.
          </div>
        )}
        {!makingNew && !form.cat && (
          <div style={{ fontFamily: 'VT323', fontSize: 16, color: '#6b3a1d', marginTop: 6 }}>
            No categories yet — plant one with + NEW.
          </div>
        )}
      </Field>
      {form.type === 'todo' && (
        <Field label="DUE DATE (OPTIONAL)">
          <DatePicker value={form.dueDate} onChange={d => update('dueDate', d)} />
          <div style={{ fontFamily: 'VT323', fontSize: 16, color: '#6b3a1d', marginTop: 6 }}>
            Your chore won't appear on Today until this day.
          </div>
        </Field>
      )}
      <Field label="NOTE (OPTIONAL)">
        <textarea
          className="pixel-input"
          rows={3}
          value={form.note}
          onChange={e => update('note', e.target.value)}
        />
      </Field>

      {err && (
        <div style={{ color: '#b83a26', fontFamily: 'VT323', fontSize: 20, marginBottom: 8 }}>
          ▸ {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
        <PixelButton onClick={onCancel} disabled={saving}>CANCEL</PixelButton>
        <PixelButton variant="gold" onClick={submit} disabled={saving}>
          {saving ? 'PLANTING…' : <><Triangle /> PLANT IT</>}
        </PixelButton>
      </div>
    </>
  );
}

function NewCategoryForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (key: string) => void;
}) {
  const { createCategory } = useStore();
  const [label, setLabel] = useState('');
  const [tint, setTint] = useState<Tint>('moss');
  const [icon, setIcon] = useState<IconSlug | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const make = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setErr('Give it a name, friend.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const cat = await createCategory({ label: trimmed, tint, icon });
      onCreated(cat.key);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: '#e8d29a',
        border: '3px solid #1a110a',
        boxShadow: '3px 3px 0 #1a110a',
      }}
    >
      <div className="section-label">NEW CATEGORY</div>
      <Field label="NAME">
        <input
          className="pixel-input"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Garden"
          maxLength={24}
          autoFocus
        />
      </Field>
      <Field label="TINT">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TINT_KEYS.map(t => (
            <Choice
              key={t}
              selected={tint === t}
              tint={t}
              onClick={() => setTint(t)}
            >
              {t.toUpperCase()}
            </Choice>
          ))}
        </div>
      </Field>
      <Field label="ICON">
        <IconPicker value={icon} onChange={setIcon} />
      </Field>
      {err && (
        <div style={{ color: '#b83a26', fontFamily: 'VT323', fontSize: 18, marginTop: 6 }}>
          ▸ {err}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
        <PixelButton onClick={onCancel} disabled={busy}>CANCEL</PixelButton>
        <PixelButton variant="gold" onClick={make} disabled={busy}>
          {busy ? 'MAKING…' : <><Triangle /> MAKE IT</>}
        </PixelButton>
      </div>
    </div>
  );
}
